import { type AccountInfo, type AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser'
import React, { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { initialMsalConfig, MsalConfig } from "../interfaces/configuration";
import { useStore } from "./app";

export interface MsalContextValue {
    instance: PublicClientApplication
    initialized: boolean
}

interface MsalContextProviderProps {
    children: ReactNode;
}

const MsalContext = createContext<MsalContextValue | null>(null);

export async function acquireTokenSilent(instance: PublicClientApplication, scopes: string[]): Promise<AuthenticationResult> {
    return await instance.acquireTokenSilent({
        scopes: scopes,
        account: instance.getActiveAccount() ?? undefined
    })
}

export const MsalContextProvider: React.FC<MsalContextProviderProps> = ({ children }: { children: ReactNode }) => {
    // Persisted instance + state
    const [instance, setInstance] = useState<PublicClientApplication | null>(null);
    const [initialized, setInitialized] = useState(false);

    const settings = useStore.getState().configuration

    useEffect(() => {
        if (!settings) return;

        // If we already have an instance for this clientId, skip recreation
        if (instance && instance.getConfiguration().auth.clientId === settings.clientId) {
            return;
        }

        const msalConfig: MsalConfig = {
            ...initialMsalConfig,
            auth: {
                ...initialMsalConfig.auth,
                clientId: settings.clientId,
                redirectUri: settings.redirectUri,
                postLogoutRedirectUri: settings.postLogoutRedirectUri
            }
        };

        setInitialized(false);

        const newInstance = new PublicClientApplication(msalConfig);
        newInstance.enableAccountStorageEvents();
        newInstance.initialize().then(() => {
            if (!newInstance.getActiveAccount() && newInstance.getAllAccounts().length > 0) {
                newInstance.setActiveAccount(newInstance.getAllAccounts()[0]);
            }
            setInitialized(true);
            setInstance(newInstance);
        });
        newInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                newInstance.setActiveAccount(event.payload as AccountInfo);
            }
        });
    }, [settings, instance]);

    if(!initialized || !instance) return null;
    return (
        <MsalContext.Provider value={{instance, initialized}}>
            {children}
        </MsalContext.Provider>
    )
}

export const useMsalInstance = (): MsalContextValue => {
    const context = useContext(MsalContext);

    if (!context) {
        throw new Error('useMsalInstance must be used within a MsalProvider');
    }

    return context;
};
